// Cửa sổ dâng từ đáy (UI_SPEC §4.1): không cuộn, ruột ≤ 560px, ≤ 5 khối, 1 số chính.
// Stack: ‹ lui một nấc (khi có cửa cha), ✕ đóng cả chuỗi, chạm nền mờ = ✕.
import type { JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { t } from '@i18n/t';
import { backWindow, closeWindows, windows, type WindowEntry } from '@state/modules/ui';
import { Icon } from '../icons';
import { PlaceholderWindow } from '../screens/Placeholder';
import { DeathSeclusionWindow, SiteWindow } from '../screens/character/cultivationWindows';
import { CodexWindow } from '../screens/destiny/CodexScreen';
import { FilterWindow, GoodsWindow, ItemDetailWindow } from '../screens/character/itemWindows';
import { AlchemyWindow, BuildingWindow, PlotWindow } from '../screens/character/caveWindows';
import { ZoneWindow } from '../screens/world/zoneWindow';
import { MarketWindow } from '../screens/affairs/marketWindow';
import { findItem } from '@state/modules/inventory';
import { goodsName, itemName } from '../itemView';

function Body({ w }: { w: WindowEntry }): JSX.Element {
  switch (w.kind) {
    case 'placeholder':
      return <PlaceholderWindow screen={w.screen} />;
    case 'site':
      return <SiteWindow />;
    case 'deathSeclusion':
      return <DeathSeclusionWindow />;
    case 'codex':
      return <CodexWindow entryId={w.entryId} />;
    case 'item':
      return <ItemDetailWindow uid={w.uid} />;
    case 'goods':
      return <GoodsWindow stackKey={w.key} />;
    case 'filter':
      return <FilterWindow />;
    case 'zone':
      return <ZoneWindow zoneId={w.zoneId} />;
    case 'plot':
      return <PlotWindow index={w.index} />;
    case 'building':
      return <BuildingWindow id={w.id} />;
    case 'alchemy':
      return <AlchemyWindow />;
    case 'market':
      return <MarketWindow mode={w.mode} />;
  }
}

function titleOf(w: WindowEntry): string {
  switch (w.kind) {
    case 'placeholder':
      return t('ui.placeholder.window.title');
    case 'site':
      return t('ui.cult.site.title');
    case 'deathSeclusion':
      return t('ui.cult.death.title');
    case 'codex':
      return t(`${w.entryId}.title`);
    // Tooltip vật phẩm: tiêu đề cửa để trống, tên chỉ đọc một lần cạnh ô (UI_SPEC §11).
    case 'item':
      return '';
    case 'goods':
      return goodsName(w.key);
    case 'filter':
      return t('ui.filter.title');
    case 'zone':
      return t(`${w.zoneId}.name`);
    case 'plot':
      return t('craft.building.herbGarden');
    case 'building':
      return t(`craft.building.${w.id}`);
    case 'alchemy':
      return t('craft.alchemy');
    case 'market':
      return t(w.mode === 'buy' ? 'ui.market.buyStall' : 'ui.market.sellStall');
  }
}

/** Nhãn đọc của cửa: tooltip vật phẩm không có tiêu đề hiện (UI_SPEC §11) nhưng vẫn đọc tên món. */
function ariaOf(w: WindowEntry): string {
  if (w.kind !== 'item') return titleOf(w);
  const f = findItem(w.uid);
  return f
    ? t('ui.item.aria', {
        name: itemName(f.item),
        rarity: t(`item.rarity.${f.item.rarity}`),
        n: f.item.giai,
      })
    : t('ui.item.gone');
}

export function WindowHost(): JSX.Element | null {
  const stack = windows.value;
  const top = stack[stack.length - 1];
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (top) closeRef.current?.focus();
  }, [top]);
  if (!top) return null;
  return (
    <div class="ht-window-layer">
      <div class="ht-scrim" data-id="window-scrim" onClick={closeWindows} />
      <section class="ht-window" role="dialog" aria-modal="true" aria-label={ariaOf(top)} data-id="window">
        <div class="ht-head">
          {stack.length > 1 && (
            <button type="button" class="ht-headbtn" aria-label={t('ui.windowBack')} onClick={backWindow}>
              <Icon name="chevronLeft" />
            </button>
          )}
          <h2>{titleOf(top)}</h2>
          <button
            ref={closeRef}
            type="button"
            class="ht-headbtn"
            data-id="window-close"
            aria-label={t('ui.close')}
            onClick={closeWindows}
          >
            <Icon name="close" />
          </button>
        </div>
        <div class="ht-window-body" data-id="window-body">
          <Body w={top} />
        </div>
      </section>
    </div>
  );
}
